import type { DesignAsset, DesignShape } from '../../types/design'
import type { MaterializationAdapter, MaterializationContext, MaterializationResult } from './index'

export type ChecklistBlueprintNodeKind = 'ITEM' | 'GATE_AND' | 'GATE_OR'

export type ChecklistBlueprintNode = {
  id: string
  kind: ChecklistBlueprintNodeKind
  title: string
  position: { x: number; y: number }
  dependencyNodeIds: string[]
  gateInputIds: string[]
}

export type ChecklistBlueprintDraft = {
  workspaceId: string
  sourceDesignAssetId: string
  title: string
  nodes: ChecklistBlueprintNode[]
  createdBy: { id: string; name: string }
  createdAt: number
}

const trim = (raw: string | undefined, fallback: string): string => {
  const value = (raw ?? '').trim()
  return value.length ? value.slice(0, 200) : fallback
}

const roleToKind = (shape: DesignShape): ChecklistBlueprintNodeKind | null => {
  switch (shape.semanticRole) {
    case 'GATE_AND':
      return 'GATE_AND'
    case 'GATE_OR':
      return 'GATE_OR'
    case 'CHECKLIST_NODE':
      return 'ITEM'
    default:
      break
  }
  if (shape.semanticRole) return null
  if (shape.type === 'DIAMOND') return 'GATE_AND'
  if (shape.type === 'PROCESS' || shape.type === 'RECT' || shape.type === 'ROUNDED_RECT') return 'ITEM'
  return null
}

const runChecklistAdapter = (
  asset: DesignAsset,
  ctx: MaterializationContext,
): MaterializationResult<ChecklistBlueprintDraft> => {
  const warnings: string[] = []
  const nodes: ChecklistBlueprintNode[] = []
  const nodeIndex = new Map<string, ChecklistBlueprintNode>()

  const sortedShapes = asset.shapes
    .filter((shape) => shape.type !== 'CONNECTOR')
    .slice()
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)

  for (const shape of sortedShapes) {
    const kind = roleToKind(shape)
    if (!kind) continue
    const node: ChecklistBlueprintNode = {
      id: shape.id,
      kind,
      title: trim(shape.text ?? shape.name, kind === 'ITEM' ? `Item ${nodes.length + 1}` : `Gate ${nodes.length + 1}`),
      position: { x: shape.position.x, y: shape.position.y },
      dependencyNodeIds: [],
      gateInputIds: [],
    }
    nodes.push(node)
    nodeIndex.set(shape.id, node)
  }

  if (nodes.length === 0) {
    return {
      ok: false,
      errors: [
        'This design has no shapes tagged as CHECKLIST_NODE / GATE_AND / GATE_OR (or eligible process/diamond fallbacks) to materialize into a checklist blueprint.',
      ],
    }
  }

  const gateCount = { and: 0, or: 0 }
  let orphanConnectorCount = 0

  for (const shape of asset.shapes) {
    if (shape.type !== 'CONNECTOR') continue
    const fromId = shape.from.kind === 'SHAPE' ? shape.from.shapeId : null
    const toId = shape.to.kind === 'SHAPE' ? shape.to.shapeId : null
    if (!fromId || !toId) { orphanConnectorCount += 1; continue }
    const target = nodeIndex.get(toId)
    const source = nodeIndex.get(fromId)
    if (!target || !source) { orphanConnectorCount += 1; continue }
    if (target.kind === 'GATE_AND' || target.kind === 'GATE_OR') {
      if (!target.gateInputIds.includes(source.id)) target.gateInputIds.push(source.id)
    } else {
      if (!target.dependencyNodeIds.includes(source.id)) target.dependencyNodeIds.push(source.id)
    }
  }

  // Ensure a stable, deterministic order for downstream diffing/tests.
  for (const node of nodes) {
    node.dependencyNodeIds.sort()
    node.gateInputIds.sort()
    if (node.kind === 'GATE_AND') gateCount.and += 1
    if (node.kind === 'GATE_OR') gateCount.or += 1
    if ((node.kind === 'GATE_AND' || node.kind === 'GATE_OR') && node.gateInputIds.length === 0) {
      warnings.push(`Gate "${node.title}" has no incoming connectors — you can add inputs after opening the checklist board.`)
    }
  }

  if (orphanConnectorCount > 0) {
    warnings.push(`${orphanConnectorCount} connector${orphanConnectorCount === 1 ? '' : 's'} could not be mapped (missing endpoints) and were skipped.`)
  }
  if (gateCount.and + gateCount.or > 0) {
    warnings.push(`Detected ${gateCount.and} AND gate${gateCount.and === 1 ? '' : 's'} and ${gateCount.or} OR gate${gateCount.or === 1 ? '' : 's'} — inputs land as gateInputIds for ChecklistDrawingBoard to review.`)
  }

  const draft: ChecklistBlueprintDraft = {
    workspaceId: ctx.workspaceId,
    sourceDesignAssetId: asset.id,
    title: trim(asset.title, 'Untitled checklist'),
    nodes,
    createdBy: ctx.actor,
    createdAt: Date.now(),
  }
  return { ok: true, draft, warnings }
}

export const checklistAdapter: MaterializationAdapter<ChecklistBlueprintDraft> = {
  kind: 'CHECKLIST_BLUEPRINT',
  supportedRoles: ['CHECKLIST_NODE', 'GATE_AND', 'GATE_OR'],
  run: runChecklistAdapter,
}
